import { defineStore } from 'pinia'

/**
 * デフォルトデザインを「グループ配列」に正規化して返す。
 * - 新形式: { groups: [{ applyProjects, frames, stamps, texts, ui, name }, ...] }
 * - 旧形式: { frames, stamps, texts } または { frames, stamps, texts, ui }
 *           → 1個のグループ ({ applyProjects: [], frames, stamps, texts, ui }) にラップ
 * - null / undefined / 空 → []
 */
function normalizeDesign(design) {
    if (!design || typeof design !== 'object') return []
    if (Array.isArray(design.groups)) {
        return design.groups
            .filter(g => g && typeof g === 'object')
            .map(g => ({
                name: g.name ?? '',
                applyProjects: Array.isArray(g.applyProjects) ? g.applyProjects : [],
                frames: Array.isArray(g.frames) ? g.frames : [],
                stamps: Array.isArray(g.stamps) ? g.stamps : [],
                texts:  Array.isArray(g.texts)  ? g.texts  : [],
                ui: (g.ui && typeof g.ui === 'object') ? g.ui : {},
            }))
    }
    // 旧フラット形式: 1グループ・全apply・制限なしとして扱う
    if (Array.isArray(design.frames) || Array.isArray(design.stamps) || Array.isArray(design.texts)) {
        return [{
            name: '',
            applyProjects: [],
            frames: Array.isArray(design.frames) ? design.frames : [],
            stamps: Array.isArray(design.stamps) ? design.stamps : [],
            texts:  Array.isArray(design.texts)  ? design.texts  : [],
            ui: (design.ui && typeof design.ui === 'object') ? design.ui : {},
        }]
    }
    return []
}

export const useDecoDataStore = defineStore('decoData', {
    state: () => ({
        frames: { styles: [], events: [], genres: [] },
        stamps: { styles: [], events: [], genres: [] },
        pictures: [],
        storageUrl: '',
        isAuthenticated: false,
        drinkNameKana: '',
        isFrameLimited: false,
        // 編集モード関連
        currentProjectId: null,
        designGroups: [], // 正規化済み groups
        selectedDesignIndex: 0, // activeDesignGroups 内で現在配置中のグループ index
        decoImages: {}, // decoId => image_url (サーバ解決済み・プロジェクト外含む)
    }),
    getters: {
        /**
         * 現在のプロジェクトに適用される design グループのみ抽出。
         * - applyProjects が空配列 → 常に適用 (全プロジェクト)
         * - applyProjects に currentProjectId が含まれる → 適用
         * - それ以外 → スキップ
         */
        activeDesignGroups: (state) => state.designGroups.filter(g => {
            if (!g.applyProjects || g.applyProjects.length === 0) return true
            return state.currentProjectId != null && g.applyProjects.includes(state.currentProjectId)
        }),
        /**
         * 切り替えUI対象となる該当グループが複数あるか
         */
        hasMultipleDesignGroups() {
            return this.activeDesignGroups.length > 1
        },
        /**
         * 現在キャンバスに配置中の design グループ (1つだけ表示・切り替え方式)
         */
        selectedDesignGroup(state) {
            const groups = this.activeDesignGroups
            if (groups.length === 0) return null
            const idx = Math.min(Math.max(state.selectedDesignIndex, 0), groups.length - 1)
            return groups[idx]
        },
        /**
         * decoId から相対画像パス (image_url) を引く。フレーム/スタンプ全グループを走査。
         * 見つからなければ ''。
         */
        decoImageUrl: (state) => (decoId) => {
            if (!decoId) return ''
            // サーバ解決済みマップを優先 (プロジェクト外フレーム/スタンプ対応)
            if (state.decoImages && state.decoImages[decoId]) return state.decoImages[decoId]
            const all = [
                ...(state.frames.styles ?? []),
                ...(state.frames.events ?? []),
                ...(state.frames.genres ?? []),
                ...(state.stamps.styles ?? []),
                ...(state.stamps.events ?? []),
                ...(state.stamps.genres ?? []),
            ]
            for (const g of all) {
                for (const d of (g.decos ?? [])) {
                    if (d.id === decoId) return d.image_url
                }
            }
            return ''
        },
        /**
         * 現在選択中グループの ui ヒント (1グループのみ表示するため OR 合成は不要)
         */
        mergedUiHints() {
            const g = this.selectedDesignGroup
            return (g && g.ui) ? g.ui : {}
        },
    },
    actions: {
        init(data) {
            this.frames = data.frames ?? { styles: [], events: [], genres: [] }
            this.stamps = data.stamps ?? { styles: [], events: [], genres: [] }
            this.pictures = data.pictures ?? []
            this.storageUrl = data.storageUrl ?? ''
            this.isAuthenticated = data.isAuthenticated ?? false
            this.drinkNameKana = data.drinkNameKana ?? ''
            this.isFrameLimited = data.context?.isFrameLimited ?? false
            this.currentProjectId = data.currentProjectId ?? null
            this.designGroups = normalizeDesign(data.defaultDesign)
            this.selectedDesignIndex = 0
            this.decoImages = (data.decoImages && typeof data.decoImages === 'object') ? data.decoImages : {}
        },
        selectDesign(index) {
            const max = this.activeDesignGroups.length - 1
            if (max < 0) return
            this.selectedDesignIndex = Math.min(Math.max(index, 0), max)
        },
    },
})
